<script lang="ts" setup>
import type { MenuItem } from 'primevue/menuitem';

const { baseUrl } = useRuntimeConfig().public;
const { data: session, status, signOut, signIn } = useAuth();
const features = useFeaturesStore();
const organisation = useOrganisationStore();

const organisationId = session.value?.user.organisationId;

await callOnce(() => organisation.loadUsers(organisationId));

const navigation = [
    {
        label: 'Home',
        route: `${baseUrl}/dashboard`,
        external: true,
        icon: 'heroicons:home',
        visible: !!session.value?.user.organisationId || session.value?.user.role === 'admin',
    },
    {
        label: 'Data Check-in',
        route: `${baseUrl}/data-checkin-pipelines`,
        external: true,
        icon: 'bi:cloud-upload',
        visible: !!session.value?.user.organisationId,
    },
    {
        label: 'My Assets',
        route: `${baseUrl}/assets`,
        external: true,
        icon: 'heroicons:squares-2x2',
        visible: !!session.value?.user.organisationId,
    },
    {
        label: 'Analytics',
        route: `${baseUrl}/analytics-pipelines`,
        external: true,
        icon: 'prime:share-alt',
        visible: features.isEnabled('analytics') && session.value?.user.organisationId,
    },
    {
        label: 'Dashboards',
        route: `/dashboards`,
        icon: 'heroicons:chart-bar-square',
        visible: features.isEnabled('dashboard.builder') && session.value?.user.organisationId,
    },
    {
        label: 'Retrieval',
        route: `${baseUrl}/retrieval`,
        external: true,
        icon: 'bi:cloud-download',
        visible:
            features.areAnyEnabled(['retrieve.api', 'retrieve.files', 'retrieve.stream']) &&
            session.value?.user.organisationId,
    },
    {
        label: 'Data Models',
        route: `${baseUrl}/models`,
        external: true,
        icon: 'prime:objects-column',
        visible:
            features.isEnabled('model-manager') &&
            ['admin', 'model-moderator'].includes(session.value?.user.role ?? ''),
    },
    {
        label: 'Admin',
        route: `${baseUrl}/admin`,
        external: true,
        icon: 'heroicons:computer-desktop',
        visible: session.value?.user.role === 'admin',
    },
];

// User Menu
const userMenu = ref();
const userProfileNavigation: MenuItem[] = [
    { label: 'Personal Profile', command: () => navigateTo('/profile'), icon: 'pi pi-user' },
    { label: 'Logout', command: () => logout(), icon: 'pi pi-sign-out' },
];
const toggleUserMenu = (event: any) => {
    userMenu.value.toggle(event);
};

const logout = async () => {
    await signOut({ callbackUrl: '/' });
};
// const gotoNotifications = () => console.log('NYI - gotoNotifications');
</script>

<template>
    <div class="flex overflow-hidden">
        <div class="flex-col flex-shrink-0 flex justify-between w-16 overflow-hidden overflow-y-auto bg-white">
            <div class="flex items-center justify-center w-full overflow-hidden bg-white border-r border-slate-100">
                <NuxtLink :to="baseUrl" class="flex-shrink-0 my-14 py-[5px] focus:outline-none">
                    <img class="-rotate-90" src="/images/logo.svg" alt="Griffo" width="81" />
                </NuxtLink>
            </div>
            <nav class="bg-primary-700 h-full w-full">
                <ul role="list" class="flex flex-col items-center space-y-2 py-2">
                    <li v-for="(item, idx) in navigation" :key="idx" class="w-full">
                        <NuxtLink
                            v-if="item.visible"
                            v-tooltip="item.label"
                            :to="item.route"
                            :external="item.external"
                            active-class="bg-white !text-primary-700"
                            class="flex flex-col items-center w-full group font-semibold rounded-tr-xl p-3 text-primary-200 hover:bg-white hover:text-primary-700"
                        >
                            <Icon :name="item.icon ?? ''" class="h-6 w-6" />
                        </NuxtLink>
                    </li>
                </ul>
            </nav>
        </div>

        <div class="flex flex-col flex-1 w-full overflow-hidden">
            <div class="flex flex-col overflow-hidden h-screen">
                <PDynamicDialog />
                <div
                    class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-surface-200 bg-white dark:border-surface-700 dark:bg-surface-900 px-6 sm:gap-x-6"
                >
                    <div class="flex flex-1 justify-between gap-x-4 self-stretch lg:gap-x-6">
                        <div id="tabs" class="flex items-center justify-center space-x-4"></div>
                        <div class="flex items-center gap-x-2">
                            <!-- Notifications Icon -->
                            <a
                                v-if="status === 'authenticated'"
                                type="button"
                                class="rounded-full p-1.5 w-9 h-9 hover:bg-neutral-200 dark:hover:bg-neutral-700"
                                :href="`${baseUrl}/notifications`"
                            >
                                <Icon name="heroicons:bell" class="text-primary-400 w-6 h-6" />
                            </a>
                            <!-- Light/Dark Mode Picker -->
                            <ColorModePicker v-if="features.isEnabled('ui.dark')" />
                            <!-- Separator -->
                            <div class="block h-6 w-px bg-surface-900/10 dark:bg-surface-700" aria-hidden="true"></div>
                            <!-- Profile dropdown -->
                            <div class="flex flex-row space-x-2 items-center">
                                <div v-if="status === 'authenticated'" class="relative">
                                    <button
                                        type="button"
                                        class="flex flex-row items-center rounded-full"
                                        aria-haspopup="true"
                                        aria-controls="user_menu"
                                        @click="toggleUserMenu"
                                    >
                                        <Gravatar
                                            class="w-8 h-8 rounded-full mr-2"
                                            :email="session?.user?.email"
                                            default-img="identicon"
                                        />
                                        <div class="text-sm font-medium text-primary-700 dark:text-neutral-200 mr-1">
                                            {{ session?.user?.name }}
                                        </div>
                                        <Icon
                                            name="prime:chevron-down"
                                            class="w-4 h-4 text-neutral-800 dark:text-neutral-200"
                                        />
                                    </button>
                                    <PMenu id="user_menu" ref="userMenu" :popup="true" :model="userProfileNavigation" />
                                </div>
                                <CommonButton
                                    v-else
                                    level="primary"
                                    colour="orange"
                                    label="Sign In"
                                    :loading="status === 'loading'"
                                    @click="signIn('keycloak')"
                                >
                                    <template #icon>
                                        <Icon name="prime:sign-in" />
                                    </template>
                                </CommonButton>
                            </div>
                        </div>
                    </div>
                </div>
                <main class="flex flex-col flex-1 flex-grow flex-shrink overflow-hidden h-screen">
                    <slot />
                </main>
            </div>
            <PToast position="bottom-right" />
        </div>
    </div>
</template>
